import CloseIcon from "@mui/icons-material/Close";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { Dialog, DialogContent, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { tableHeaderProps } from "styled/props/tableProps";

import { MOBILE_MEDIA_QUERY } from "../../../../constants/mediaQueries";
import { downloadAttachments } from "../../../../services/attachment/attachmentAPIs";
import styledDialogueSX from "../../../../styled/styledSXProperties/styledDialogueSX";

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.primary, // Set link color based on theme
    textDecoration: "underline",
    "&:hover": {
        textDecoration: "none", // Remove underline on hover
    },
    cursor: "pointer",
}));

const StyledTable = styled(Table)({
    width: "100%",
});

const StyledTableCell = styled(TableCell)({
    padding: "16px",
});

const AttachmentsModal = ({ attachments, setOpen, open, title }) => {
    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);
    const dispatch = useDispatch();
    const handleDownload = useCallback(async (event, url, title) => {
        event.stopPropagation();
        const response = await downloadAttachments(url, dispatch);
        const urlCreator = window.URL || window.webkitURL;
        const link = document.createElement("a");
        link.href = urlCreator.createObjectURL(response.data);
        link.setAttribute("download", title);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);
    return (
        <Dialog
            open={open}
            fullScreen={!isDesktop}
            onClose={(event) => {
                event.stopPropagation();
                setOpen(false);
            }}
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <DialogContent sx={styledDialogueSX}>
                <Grid container spacing={2} marginBottom="16px">
                    <Grid item xs={11}>
                        <Typography
                            variant="h3"
                            marginBottom={"4px"}
                            data-testid="attachments-modal-header"
                        >
                            {title}
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        item
                        xs={1}
                        justifyContent="flex-end"
                        alignContent="center"
                    >
                        <IconButton
                            data-testid="attachments-modal-close-icon"
                            onClick={(event) => {
                                event.stopPropagation();
                                setOpen(false);
                            }}
                            // sx={{ mt: "-25px" }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                <TableContainer>
                    <StyledTable>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>
                                    <Typography
                                        {...{
                                            ...tableHeaderProps,
                                            align: "center",
                                        }}
                                    >
                                        File Name
                                    </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography
                                        {...{
                                            ...tableHeaderProps,
                                            align: "center",
                                        }}
                                    >
                                        Title
                                    </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography
                                        {...{
                                            ...tableHeaderProps,
                                            align: "center",
                                        }}
                                    >
                                        Description
                                    </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography
                                        {...{
                                            ...tableHeaderProps,
                                            align: "center",
                                        }}
                                    >
                                        Category
                                    </Typography>
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attachments.map((row) => (
                                <TableRow
                                    key={row?.ATTACHMENT_ID}
                                    data-testid="modal-table-row"
                                >
                                    <StyledTableCell
                                        align="center"
                                        data-testid="modal-cell-title"
                                    >
                                        <StyledLink
                                            data-testid="modal-cell-download-pdf"
                                            onClick={(event) =>
                                                handleDownload(
                                                    event,
                                                    row?.INTERNAL_URL,
                                                    row?.FILENAME
                                                )
                                            }
                                        >
                                            <Typography
                                                style={{
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-all",
                                                    overflowWrap: "break-word",
                                                }}
                                            >
                                                {row?.FILENAME}
                                            </Typography>
                                        </StyledLink>
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        width={100}
                                        data-testid="modal-cell-text"
                                        style={{
                                            whiteSpace: "normal",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {row?.TITLE}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        data-testid="modal-cell-description"
                                        style={{
                                            whiteSpace: "normal",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {row?.DESCRIPTION}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        data-testid="modal-cell-category"
                                    >
                                        {Array.isArray(row?.CATEGORY)
                                            ? row.CATEGORY.join(", ")
                                            : row.CATEGORY}
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};
AttachmentsModal.propTypes = {
    attachments: PropTypes.array,
    setOpen: PropTypes.func,
    open: PropTypes.bool,
    title: PropTypes.string,
};
export default AttachmentsModal;
